"use client";

import { cn } from "@borderless/ui/lib/utils";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useCallback, useContext, useEffect, useState } from "react";
import { PageTransitionContext } from "./PageTransitionProvider";

const PageTransitionIndicator = () => {
  const [isTransitioning, setIsTransitioning] = useContext(
    PageTransitionContext,
  );
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // when the route change happens instantly, isTransitioning stays true after the transition is complete
    setIsTransitioning(false);
  }, [pathname, searchParams]);

  const [progress, setProgress] = useState(0);

  const incrementProgress = useCallback((currentProgress: number) => {
    if (currentProgress >= 0 && currentProgress < 10) {
      return currentProgress + 5;
    } else if (currentProgress >= 10 && currentProgress < 30) {
      return currentProgress + 3;
    } else if (currentProgress >= 30 && currentProgress < 50) {
      return currentProgress + 2;
    } else if (currentProgress >= 50 && currentProgress < 70) {
      return currentProgress + 1;
    } else if (currentProgress >= 70 && currentProgress < 90) {
      return currentProgress + 0.5;
    } else if (currentProgress >= 90 && currentProgress < 99) {
      return currentProgress + 0.1;
    }
    return 99;
  }, []);

  const [showIndicator, setShowIndicator] = useState(false);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    let hideTimeoutId: NodeJS.Timeout | undefined;

    if (isTransitioning) {
      setShowIndicator(true);
      setProgress(0);
      intervalId = setInterval(() => {
        setProgress((prevProgress) => incrementProgress(prevProgress));
      }, 100);
    } else {
      hideTimeoutId = setTimeout(
        () => {
          setShowIndicator(false);
        },
        progress < 10 ? 0 : 300,
      ); // Delay hiding the indicator

      setProgress(100);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (hideTimeoutId) clearTimeout(hideTimeoutId);
    };
  }, [isTransitioning, incrementProgress]);

  return (
    <div
      className={cn(
        "fixed top-0 right-0 left-0 z-50 h-1 w-full overflow-hidden",
        showIndicator ? "block" : "hidden",
      )}
    >
      <div
        className="h-full w-full transform bg-blue-500 transition-all duration-200 ease-out"
        style={{ transform: `translateX(${progress - 100}%)` }}
      />
    </div>
  );
};

const PageTransitionIndicatorWithSuspense = () => {
  return (
    <Suspense>
      <PageTransitionIndicator />
    </Suspense>
  );
};

export default PageTransitionIndicatorWithSuspense;
